import { ReactNode, useEffect, useRef, useState } from "react";
import { Box, Container, Flex } from "@chakra-ui/react";
import BundleIcon from "components/ui/BundleIcon";
import SplitType from "split-type";
import { MotionBox } from "utils/framer/motionbox";
import { Field, LinkField, useSitecoreContext } from "@sitecore-jss/sitecore-jss-nextjs";
import SCLinkButton from "components/sc/SCLinkButton";
import SCHeading from "components/sc/SCHeading";
import SCText from "components/sc/SCText";
import ghHeadlines from "themes/utils/mappings/gh-headline-size";
import { gtmCTAEvent } from "components/head/GTMTracking";

export interface GenericHeaderProps {
    Id?: string;
    HeaderHeadline?: Field<string>;
    HeaderSubHeadline?: Field<string>;
    HeaderCTA?: LinkField;
    headlineSize?: string;
    children: ReactNode;
}

const GenericHeader = (props: GenericHeaderProps) => {
    const {
        sitecoreContext: { pageEditing },
    } = useSitecoreContext();

    const HeaderTitleRef = useRef<HTMLHeadingElement>(null);
    const [isShown, setIsShown] = useState(false);
    const GenericHeaderRef = useRef<HTMLDivElement>(null);

    const headingSize = props.headlineSize
        ? ghHeadlines[props.headlineSize]
        : ghHeadlines["{117C649E-9E4A-4D15-993D-3D094EBA9A0C}"];

    const container = useRef<HTMLDivElement>(null);

    const modifyLineSplitting = (
        unSplittedText: string | undefined,
        text: SplitType,
        ref: React.MutableRefObject<HTMLElement | null>,
        transitionEndCallbackFun?: (() => any) | undefined
    ) => {
        text.lines?.map((line, index) => {
            line.innerHTML = `<span class="innerLine" style="transition-delay:${
                0.1 * (index + 1)
            }s;">${line.innerHTML}</span>`;
        });
        const lastLine = text.lines?.[text.lines.length - 1].querySelector(".innerLine");
        lastLine?.addEventListener("transitionend", () => {
            if (ref.current && unSplittedText) {
                ref.current.innerHTML = unSplittedText;
            }
            if (transitionEndCallbackFun) {
                transitionEndCallbackFun();
            }
        });
    };

    useEffect(() => {
        if (!pageEditing && HeaderTitleRef.current) {
            const text = new SplitType(HeaderTitleRef.current, {
                types: "lines",
                tagName: "span",
            });

            modifyLineSplitting(props.HeaderHeadline?.value, text, HeaderTitleRef);
        }
    }, [pageEditing, props.HeaderHeadline?.value]);

    const isNotEmpty = (value: string | undefined) => value !== undefined && value?.trim() !== "";

    const hasContent =
        pageEditing ||
        (!pageEditing && isNotEmpty(props.HeaderHeadline?.value)) ||
        isNotEmpty(props.HeaderSubHeadline?.value) ||
        isNotEmpty(props.HeaderCTA?.value?.href);

    const showHeading = pageEditing || (!pageEditing && isNotEmpty(props.HeaderHeadline?.value));

    const showTextAndCta =
        pageEditing ||
        (!pageEditing &&
            (isNotEmpty(props.HeaderSubHeadline?.value) ||
                isNotEmpty(props.HeaderCTA?.value?.href)));

    return (
        <Container
            variant={"module"}
            ref={container}
            {...(!hasContent && {
                sx: {
                    ":not(:has(div)), :not(:has(div div))": {
                        py: 0,
                    },
                },
            })}
        >
            {hasContent && (
                <>
                    {showHeading && (
                        <Box position="relative" ref={GenericHeaderRef}>
                            <MotionBox
                                textAlign={["center", null, null, "left"]}
                                flex="0 0 auto"
                                viewport={{ once: true }}
                                onViewportEnter={() => {
                                    setIsShown(true);
                                }}
                            >
                                <SCHeading
                                    ref={HeaderTitleRef}
                                    textField={props.HeaderHeadline}
                                    as="h2"
                                    variant="moduleHeaderTitle"
                                    size={headingSize}
                                    id={`GH_${props.Id}`}
                                    left={0}
                                    right={0}
                                    overflow="hidden"
                                    mb={["sp48", null, null, "sp96"]}
                                    sx={{
                                        wordWrap: "normal",

                                        span: {
                                            wordWrap: "normal",
                                        },
                                        ".line": {
                                            height: "1em",
                                            overflow: "hidden",
                                        },
                                        ".innerLine": {
                                            display: "block",
                                            transform: `translateY(${isShown ? 0 : "-1.1em"})`,
                                            transition: "transform 0.8s ease-out",
                                        },
                                    }}
                                />
                            </MotionBox>
                        </Box>
                    )}

                    {showTextAndCta && (
                        <Flex
                            flexDir={["column", null, null, "row"]}
                            justifyContent="space-between"
                            alignItems={["center", null, null, "flex-end"]}
                            mb={["sp64", null, null, "sp96"]}
                        >
                            <Box w={["100%", null, null, "60%"]} flex="0 0 auto">
                                <SCText
                                    textField={props.HeaderSubHeadline}
                                    size="h3"
                                    variant="moduleHeaderText"
                                    textAlign={["center", null, null, "left"]}
                                />
                            </Box>
                            <SCLinkButton
                                linkField={props.HeaderCTA}
                                variant="outline"
                                mt={[
                                    props.HeaderSubHeadline?.value.trim() ? "sp40" : 0,
                                    null,
                                    null,
                                    0,
                                ]}
                                rightIcon={<BundleIcon name="ArrowForward" />}
                                onClick={() => {
                                    gtmCTAEvent(
                                        props.HeaderCTA?.value?.title ??
                                            props.HeaderCTA?.value?.text ??
                                            "",
                                        "Generic header CTA",
                                        props.HeaderCTA?.value?.href
                                    );
                                }}
                            />
                        </Flex>
                    )}
                </>
            )}

            {props.children}
        </Container>
    );
};

export default GenericHeader;
